import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Megaphone, X, ArrowRight } from "lucide-react";

const DISMISS_KEY = "ir_dismissed_announcement";

export const AnnouncementBanner = () => {
  const [ann, setAnn] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    api.get("/announcements/active").then(({ data }) => {
      if (!data) return;
      setAnn(data);
      const dismissed = localStorage.getItem(DISMISS_KEY);
      // Reappears whenever the admin edits it (updated_at changes the signature).
      setHidden(dismissed === `${data.id}:${data.updated_at}`);
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    if (ann) localStorage.setItem(DISMISS_KEY, `${ann.id}:${ann.updated_at}`);
    setHidden(true);
  };

  return (
    <AnimatePresence>
      {ann && !hidden && (
        <motion.div
          initial={{ opacity: 0, y: -12, height: 0 }}
          animate={{ opacity: 1, y: 0, height: "auto" }}
          exit={{ opacity: 0, y: -12, height: 0 }}
          transition={{ duration: 0.4 }}
          className="relative overflow-hidden rounded-xl blue-card text-white soft-shadow"
          data-testid="announcement-banner"
        >
          <div className="flex items-start gap-4 p-5 sm:p-6">
            <div className="shrink-0 w-11 h-11 rounded-lg bg-[var(--ir-orange)] flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0 pr-6">
              <h3 className="font-display text-2xl leading-tight" data-testid="announcement-title">{ann.title}</h3>
              {ann.body && <p className="text-white/85 text-sm mt-1.5 leading-relaxed" data-testid="announcement-body">{ann.body}</p>}
              {ann.link_url && (
                <a
                  href={ann.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] transition-colors text-white text-sm font-semibold px-4 py-2 rounded-none"
                  data-testid="announcement-link-btn"
                >
                  {ann.link_text || "Learn more"} <ArrowRight className="w-4 h-4" />
                </a>
              )}
            </div>
            <button
              onClick={dismiss}
              className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
              aria-label="Dismiss announcement"
              data-testid="announcement-dismiss-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
